import type { Table } from '@tanstack/react-table';
import * as React from 'react';
import { DateRange } from 'react-day-picker';

import { convertUrlStringDate } from '@eluve/date-utils';

// import { DataTableViewOptions } from '@/components/data-table/data-table-view-options';
import { Button } from '../Button';
import { DateRangeSelector } from '../DateRangeSelector';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Stack } from '../Stack';
import { cn } from '../utils/cn';

import { DataTableFacetedFilter } from './DataTableFacetedFilter';
import type { DataTableFilterField } from './types';

interface DataTableToolbarProps<
  TData,
> extends React.HTMLAttributes<HTMLDivElement> {
  table: Table<TData>;
  /**
   * An array of filter field configurations for the data table.
   * When options are provided, a faceted filter is rendered.
   * Otherwise, a search filter is rendered.
   *
   * @example
   * const filterFields = [
   *   {
   *     id: 'name',
   *     label: 'Name',
   *     placeholder: 'Filter by name...'
   *   },
   *   {
   *     id: 'status',
   *     label: 'Status',
   *     options: [
   *       { label: 'Active', value: 'active', icon: ActiveIcon, count: 10 },
   *       { label: 'Inactive', value: 'inactive', icon: InactiveIcon, count: 5 }
   *     ]
   *   }
   * ]
   */
  filterFields?: DataTableFilterField<TData>[];
  /**
   * Custom actions to render on the left side of the toolbar, before filters.
   * Useful for action buttons that should appear before filter controls.
   */
  leftActions?: React.ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  filterFields = [],
  leftActions,
  children,
  className,
  ...props
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  // Memoize computation of searchableColumns, filterableColumns, and dateRangeColumns
  const { searchableColumns, filterableColumns, dateRangeColumns } =
    React.useMemo(() => {
      return {
        searchableColumns: filterFields.filter((field) => {
          // Explicit type takes precedence
          if (field.type === 'search') return true;
          if (field.type === 'faceted' || field.type === 'dateRange')
            return false;
          // Infer: if no options and no explicit type, it's a search filter
          return !field.options;
        }),
        filterableColumns: filterFields.filter((field) => {
          // Explicit type takes precedence
          if (field.type === 'faceted') return true;
          if (field.type === 'search' || field.type === 'dateRange')
            return false;
          // Infer: if options are provided, it's a faceted filter
          return Boolean(field.options);
        }),
        dateRangeColumns: filterFields.filter((field) => {
          // Explicit type takes precedence
          if (field.type === 'dateRange') return true;
          // Don't infer dateRange - it must be explicitly set
          return false;
        }),
      };
    }, [filterFields]);

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-2 overflow-auto p-1',
        !leftActions && filterFields.length === 0 && 'hidden',
        className,
      )}
      {...props}
    >
      <Stack
        className="flex-1"
        direction="horizontal"
        wrap
        align="center"
        gap={2}
      >
        {leftActions}
        {searchableColumns.length > 0 &&
          searchableColumns.map(
            (column) =>
              table.getColumn(column.id ? String(column.id) : '') && (
                <Input
                  key={String(column.id)}
                  placeholder={
                    column.placeholder ?? `Filter by ${column.label}`
                  }
                  value={
                    (table
                      .getColumn(String(column.id))
                      ?.getFilterValue() as string) ?? ''
                  }
                  onChange={(event) =>
                    table
                      .getColumn(String(column.id))
                      ?.setFilterValue(event.target.value)
                  }
                  className="h-8 w-40 lg:w-64"
                />
              ),
          )}
        {filterableColumns.length > 0 &&
          filterableColumns.map(
            (column) =>
              table.getColumn(column.id ? String(column.id) : '') && (
                <DataTableFacetedFilter
                  key={String(column.id)}
                  column={table.getColumn(column.id ? String(column.id) : '')}
                  title={column.label}
                  options={column.options ?? []}
                />
              ),
          )}
        {dateRangeColumns.length > 0 &&
          dateRangeColumns.map((column) => {
            const tableColumn = table.getColumn(
              column.id ? String(column.id) : '',
            );
            if (!tableColumn) return null;

            const filterValue = tableColumn.getFilterValue() as
              | string
              | string[]
              | undefined;
            let dateRange: DateRange | undefined;
            if (filterValue) {
              try {
                const valueString =
                  typeof filterValue === 'string'
                    ? filterValue
                    : Array.isArray(filterValue)
                      ? filterValue[0]
                      : undefined;
                if (valueString) {
                  // parse underscore-separated date format: "YYYY-MM-DD_YYYY-MM-DD" or "YYYY-MM-DD_" or "_YYYY-MM-DD"
                  const [fromStr, toStr] = valueString.split('_');
                  dateRange = {
                    from: fromStr ? convertUrlStringDate(fromStr) : undefined,
                    to: toStr ? convertUrlStringDate(toStr) : undefined,
                  };
                }
              } catch {
                // invalid date format, ignore
              }
            }

            return (
              <DateRangeSelector
                key={String(column.id)}
                dateRange={dateRange}
                onDateRangeChange={(range) => {
                  if (range?.from || range?.to) {
                    // serialize as underscore-separated date format: "YYYY-MM-DD_YYYY-MM-DD"
                    // format dates as YYYY-MM-DD (ISO date format without time)
                    const fromStr = range.from
                      ? range.from.toISOString().split('T')[0]
                      : '';
                    const toStr = range.to
                      ? range.to.toISOString().split('T')[0]
                      : '';
                    const serialized = `${fromStr}_${toStr}`;
                    tableColumn.setFilterValue(serialized);
                  } else {
                    tableColumn.setFilterValue(undefined);
                  }
                }}
                align="start"
                placeholder={column.placeholder ?? column.label}
                renderTrigger={(formattedDateRange) => (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-dashed"
                  >
                    <Icon name="Calendar" size="sm" />
                    {formattedDateRange}
                  </Button>
                )}
              />
            );
          })}
        {isFiltered && (
          <Button
            aria-label="Reset filters"
            variant="outline"
            className="h-8 px-2 lg:px-3"
            onClick={() => table.resetColumnFilters()}
          >
            Reset
            <Icon name="X" className="ml-2" />
          </Button>
        )}
      </Stack>
      <Stack direction="horizontal" align="center" gap={2} fullWidth={false}>
        {children}
      </Stack>
    </div>
  );
}
